import { Client, Room } from "colyseus.js";
import { DEFAULT_WEAPON_UPGRADES } from "@neon-arena/shared";
import type { ChatMessageView, DummyPreset, GameMode, VehicleBlueprint, WeaponUpgrade } from "@neon-arena/shared";

export interface PlayerView {
  id: string;
  name: string;
  blueprintId: string;
  x: number;
  y: number;
  z: number;
  rotY: number;
  vx: number;
  vy: number;
  vz: number;
  health: number;
  maxHealth: number;
  score: number;
  alive: boolean;
  reloadRemaining: number;
  lives: number;
  eliminated: boolean;
  nextRespawnAt: number;
  boostEnergy: number;
  boostMax: number;
  teamId: number;
  lastFiredAt: number;
  deaths: number;
  damageFront: number;
  damageBody:  number;
  damageRear:  number;
  mineCount: number;
  homingMissiles: number;
  wideMineActive: boolean;
  // Timed power-up effects. Optional so older fixtures/servers stay valid;
  // snapshot() always fills them (0 = inactive).
  /** Over-armour pool remaining (armour power-up). */
  armour?: number;
  /** Server epoch ms until which overdrive is active. */
  overdriveUntil?: number;
  /** Server epoch ms until which the 5-way fan is active. */
  spread5Until?: number;
  // Weapons overhaul (optional for older fixtures; snapshot() always fills).
  /** Secondary-slot homing rocket ammo. */
  rocketAmmo?: number;
  /** Ricochet pickup shots carried on the primary trigger. */
  ricochetShots?: number;
  /** Shockwave pickup charges carried on the primary trigger. */
  shockwaveCharges?: number;
  /** ms remaining on the secondary (fire2) cooldown. */
  reloadRemaining2?: number;
}

export interface PowerUpView {
  id: string;
  type: string;
  x: number;
  z: number;
  available: boolean;
}

export interface MineView {
  id: string;
  ownerId: string;
  x: number;
  z: number;
  deployedAt: number;
}

export interface ProjectileView {
  id: string;
  ownerId: string;
  x: number;
  y: number;
  z: number;
  dx: number;
  dy: number;
  dz: number;
  speed: number;
  spawnedAt: number;
  isHoming: boolean;
  weaponType: string;
  /** Session id of the player a homing rocket is locked onto ("" = none).
   * Already on the wire (ServerGameState.ProjectileState) — surfaced for the
   * announcer's incoming-rocket warning. */
  homingTargetId: string;
}

export interface NetworkSnapshot {
  serverTime: number;
  ownId: string;
  players: Map<string, PlayerView>;
  projectiles: Map<string, ProjectileView>;
  mines: Map<string, MineView>;
  powerUps: Map<string, PowerUpView>;
  recentFx: Array<{ kind: string; x: number; y: number; z: number; createdAt: number }>;
  teamScores: { 1: number; 2: number };
}

interface ClientStateUploadPayload {
  x: number;
  y: number;
  z: number;
  rotY: number;
  vx: number;
  vy: number;
  vz: number;
  fire: boolean;
  fire2?: boolean;
  boost: boolean;
  dropWeapon: boolean;
}

// ── Queue wire payloads ──────────────────────────────────────────────────────
// These mirror, field for field, what `server/src/rooms/QueueArenaRoom.ts`
// actually sends. They were read off the `broadcastAll(...)` / `safeSend(...)`
// call sites in that file, NOT from the plan docs. Do not "improve" the names
// here — they are the wire contract.

export type QueuePhase = "lobby" | "countdown" | "match" | "intermission";

/** `queueState` — QueueArenaRoom.setPhase(), broadcast to everyone on every phase flip. */
export interface QueueStatePayload {
  phase: QueuePhase;
  /** Milliseconds remaining in this phase at send time. 0 = open-ended. */
  inMs: number;
  /** Epoch ms this phase ends. 0 = open-ended. */
  phaseEndsAt: number;
}

/** `queuePos` — QueueArenaRoom.pushQueueInfo(), sent to WAITERS only. */
export interface QueuePosPayload {
  /** 1-based position in the queue. */
  pos: number;
  /** Total number of waiters. */
  of: number;
  /** Milliseconds until the next round is expected to start. */
  etaMs: number;
}

/** `seat` — QueueArenaRoom.seatFromWaitlist() / unseat(), sent to one client. */
export interface SeatPayload {
  seated: boolean;
  /** Present when seated: the room's human seat count. */
  seats?: number;
  /** Present when unseated: e.g. "winner-stays-on". */
  reason?: string;
}

/** One entry of the `start` roster. `isBot` is derived server-side from seat ownership. */
export interface QueueRosterEntry {
  sessionId: string;
  name: string;
  teamId: number;
  isBot: boolean;
}

/** `start` — QueueArenaRoom.startRound(), broadcast; also replayed to mid-round joiners. */
export interface QueueStartPayload {
  roster: QueueRosterEntry[];
  config: {
    mode: string;
    lives: number;
    durationS: number;
    scoreLimit: number;
    startedAt: number;
    endsAt: number;
  };
}

/** One row of `roundEnd.standings` — ArenaRoom.buildStandings() (bots excluded, score desc). */
export interface RoundStandingView {
  sessionId: string;
  name: string;
  score: number;
  deaths: number;
  teamId: number;
  eliminated: boolean;
}

/** `roundEnd` — QueueArenaRoom.endRound(), broadcast. */
export interface RoundEndPayload {
  reason: string;
  standings: RoundStandingView[];
  nextInMs: number;
  nextStartAt: number;
}

// ── Hosted-lobby wire payloads ───────────────────────────────────────────────
// These mirror, field for field, what `server/src/rooms/HostedArenaRoom.ts`
// actually sends — read off the `send`/`broadcast` call sites in that file,
// NOT from the plan docs (the docs describe a schema-based roster; the real
// room pushes plain messages). Do not "improve" the names — wire contract.

export type HostedLobbyPhase = "lobby" | "match" | "post";

export type HostedMatchType = "deathmatch" | "lms" | "team-battle" | "free-drive";

/** One roster row — `LobbyMember` on the server. */
export interface LobbyMemberPayload {
  sessionId: string;
  name: string;
  ready: boolean;
  isLeader: boolean;
  /** 0 = auto/FFA pre-match; the real teamId once seated. */
  team: number;
}

/** `LobbySettings` on the server. Leader-owned; server clamps every field. */
export interface LobbySettingsPayload {
  matchType: HostedMatchType;
  roundSeconds: number;
  /** Kills that end a deathmatch round early. 0 = timer only. */
  scoreLimit: number;
  botFill: boolean;
  /** 2–8. */
  maxPlayers: number;
}

/**
 * `welcome` — sent to one client after its `hello`. Three shapes share it:
 *  - accepted:  {isLeader, rejected: null, phase, code, settings}
 *  - rejected:  {isLeader: false, rejected: "in-progress", phase} and the
 *               server then force-leaves the client
 *  - demotion:  {isLeader: false, phase} when a preferLeader hello displaces
 *               an arrival-order leader (no `rejected` field at all)
 */
export interface HostedWelcomePayload {
  isLeader: boolean;
  rejected?: "in-progress" | null;
  phase: HostedLobbyPhase;
  code?: string | null;
  settings?: LobbySettingsPayload;
}

/** `roster` — broadcast on every roster/settings/phase change (but NOT during `match`). */
export interface HostedRosterPayload {
  members: LobbyMemberPayload[];
  leaderSessionId: string | null;
  phase: HostedLobbyPhase;
  settings: LobbySettingsPayload;
  code: string | null;
}

/** `leaderLeft` — broadcast to everyone EXCEPT the inheritor (who gets `youAreLeader`). */
export interface LeaderLeftPayload {
  newLeaderSessionId: string | null;
}

/** `lobbyStartRejected` — sent to the leader only. `graceEndsAt` is a SERVER epoch. */
export interface LobbyStartRejectedPayload {
  reason: "not-enough-players" | "not-ready";
  graceEndsAt: number;
}

/** `queueState` from the hosted room — `{phase, inMs}` only (no `phaseEndsAt`). */
export interface HostedQueueStatePayload {
  phase: HostedLobbyPhase;
  inMs: number;
}

/** `start` — broadcast at match start. `config` = settings + server-epoch `endsAt`. */
export interface HostedStartPayload {
  roster: LobbyMemberPayload[];
  config: LobbySettingsPayload & { endsAt: number };
}

/**
 * `roundEnd` from the hosted room. NOTE: unlike the queue room there is no
 * relative `nextInMs` — `nextStartAt` is a server epoch. The accompanying
 * `queueState {phase:"post", inMs}` broadcast carries the skew-proof timer.
 */
export interface HostedRoundEndPayload {
  standings: RoundStandingView[];
  reason: string;
  nextStartAt: number;
}

/** Everything needed to create or join a hosted room (identity only — no loadout). */
export interface HostedConnectOptions {
  serverUrl: string;
  driverName: string;
  authToken?: string | null;
}

/** ws(s):// → http(s):// for the REST endpoints that live next to the game port. */
export function wsToHttpBase(url: string): string {
  return url.replace(/^wss?:\/\//, (m) => (m === "wss://" ? "https://" : "http://")).replace(/\/+$/, "");
}

/**
 * POST /api/lobby/resolve-code — `{code}` → `{roomId}`. 404 means unknown OR
 * expired (the server deliberately does not distinguish). Exported with an
 * injectable fetch so the lookup is unit-testable.
 */
export async function resolveHostedCode(
  serverUrl: string,
  code: string,
  fetchImpl: typeof fetch = fetch,
): Promise<string> {
  let res: Response;
  try {
    res = await fetchImpl(`${wsToHttpBase(serverUrl)}/api/lobby/resolve-code`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    });
  } catch {
    throw new Error("Server not responding — try again in a moment");
  }
  if (res.status === 404) throw new Error("Unknown or expired code — check it and try again");
  if (!res.ok) throw new Error(`Code lookup failed (HTTP ${res.status})`);
  const body = (await res.json()) as { roomId?: unknown };
  if (typeof body?.roomId !== "string" || body.roomId.length === 0) {
    throw new Error("Code lookup failed — bad server response");
  }
  return body.roomId;
}

/**
 * Everything needed to establish the room connection. Deliberately excludes
 * anything that only matters once the player actually spawns — see
 * {@link EnterMatchOptions}.
 */
export interface ConnectOptions {
  serverUrl: string;
  driverName: string;
  dummyOpponent?: boolean;
  dummyPreset?: DummyPreset;
  waypointThrottle?: number;
  dummyDifficulty?: string;
  gameMode?: GameMode;
  authToken?: string | null;
  /**
   * Which room family to join. "direct" (default) keeps the legacy drop-in
   * mapping (gameMode → arena-* room name); "queue" joins the always-on
   * quickmatch queue, which never spawns you on `hello`.
   */
  target?: "direct" | "queue";
}

/** Room name for a connect target. Exported for tests — no side effects. */
export function roomNameFor(target: "direct" | "queue", gameMode: GameMode): string {
  if (target === "queue") return "queue";
  return gameMode === "free-drive" ? "arena-free-drive"
    : gameMode === "team-battle" ? "arena-team-battle"
    : "arena-lms";
}

/** Everything the server needs in order to seat + spawn the local player. */
export interface EnterMatchOptions {
  driverName: string;
  blueprint: VehicleBlueprint;
  weaponUpgrades?: WeaponUpgrade;
  preferredTeam?: number;
  /**
   * Hosted lobbies only: marks this hello as the HOST device's leader claim
   * (`HostedArenaRoom` honours it even if the hello loses the arrival race).
   * Omitted entirely from the payload when false/undefined, so queue and
   * drop-in hellos stay byte-for-byte what they always were.
   */
  preferLeader?: boolean;
}

export class ClientNetwork {
  private client!: Client;
  private room: Room | null = null;
  private latestFxSeen = new Set<string>();
  private chatListeners: Array<(msg: ChatMessageView) => void> = [];

  // Queue event surface. Listeners are held on the instance (not on the room)
  // so callers can subscribe before OR after connect() without ordering bugs.
  private queueStateListeners: Array<(p: QueueStatePayload) => void> = [];
  private queuePosListeners:   Array<(p: QueuePosPayload) => void> = [];
  private seatListeners:       Array<(p: SeatPayload) => void> = [];
  private startListeners:      Array<(p: QueueStartPayload) => void> = [];
  private roundEndListeners:   Array<(p: RoundEndPayload) => void> = [];

  // Hosted-lobby event surface — same instance-held pattern as the queue's.
  // Kept SEPARATE from the queue listeners even where message names collide
  // (`queueState`/`start`/`roundEnd`): the payload shapes differ per room family
  // and a subscriber should never have to guess which room it is hearing.
  private hostedWelcomeListeners:       Array<(p: HostedWelcomePayload) => void> = [];
  private lobbyCodeListeners:           Array<(p: { code: string }) => void> = [];
  private hostedRosterListeners:        Array<(p: HostedRosterPayload) => void> = [];
  private youAreLeaderListeners:        Array<() => void> = [];
  private leaderLeftListeners:          Array<(p: LeaderLeftPayload) => void> = [];
  private lobbyStartRejectedListeners:  Array<(p: LobbyStartRejectedPayload) => void> = [];
  private hostedQueueStateListeners:    Array<(p: HostedQueueStatePayload) => void> = [];
  private hostedStartListeners:         Array<(p: HostedStartPayload) => void> = [];
  private hostedRoundEndListeners:      Array<(p: HostedRoundEndPayload) => void> = [];
  /** Fires on room disconnection. `byUs` = our own leave(); anything else is the server's doing. */
  private hostedLeaveListeners:         Array<(code: number, byUs: boolean) => void> = [];
  /** True from the moment our own leave() runs — lets onLeave tell a kick from a quit. */
  private leftByUs = false;

  /**
   * Join (or create) the room and wire up handlers. Does NOT spawn the local
   * player — call {@link enterMatch} for that. The drop-in path calls both back
   * to back; the queue/lobby flow will wait for a server seat-grant in between.
   */
  async connect(opts: ConnectOptions): Promise<void> {
    const {
      serverUrl,
      driverName,
      dummyOpponent = false,
      dummyPreset = "circle",
      waypointThrottle,
      dummyDifficulty,
      gameMode = "lms",
      authToken = null,
      target = "direct",
    } = opts;
    this.client = new Client(serverUrl);
    const roomName = roomNameFor(target, gameMode);
    // `name` here is the ROOM-level name. It must match the `name` sent in the
    // `hello` payload by enterMatch(), or room-level and player-level names
    // diverge (lobby-upgrade-plan §8b gotcha 5).
    const joinPromise = this.client.joinOrCreate(roomName, { name: driverName, dummy: dummyOpponent, dummyPreset, waypointThrottle, dummyDifficulty, token: authToken });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Server not responding — try again in a moment")), 12_000),
    );
    this.room = await Promise.race([joinPromise, timeoutPromise]);

    this.room.onMessage("validation-error", (msg: { errors: string[] }) => {
      console.warn("server rejected blueprint:", msg.errors);
    });

    // Queue lifecycle. Registered unconditionally: the legacy arena-* rooms
    // simply never send these, so the handlers stay dormant there.
    this.room.onMessage("queueState", (p: QueueStatePayload) => { for (const fn of this.queueStateListeners) fn(p); });
    this.room.onMessage("queuePos",   (p: QueuePosPayload)   => { for (const fn of this.queuePosListeners) fn(p); });
    this.room.onMessage("seat",       (p: SeatPayload)       => { for (const fn of this.seatListeners) fn(p); });
    this.room.onMessage("start",      (p: QueueStartPayload) => { for (const fn of this.startListeners) fn(p); });
    this.room.onMessage("roundEnd",   (p: RoundEndPayload)   => { for (const fn of this.roundEndListeners) fn(p); });

    this.subscribeChat(this.room);
  }

  /** Forward the room's chat schema into the instance-held chat listeners. */
  private subscribeChat(room: Room): void {
    const state: any = room.state;
    if (state?.chat?.onAdd) {
      state.chat.onAdd((msg: any) => {
        const view: ChatMessageView = {
          playerName: String(msg.playerName ?? ""),
          text: String(msg.text ?? ""),
          timestamp: Number(msg.timestamp ?? 0),
        };
        for (const fn of this.chatListeners) fn(view);
      });
    }
  }

  /**
   * Send `hello` — this is what makes the server seat and spawn us. Requires a
   * prior successful {@link connect}.
   */
  enterMatch(opts: EnterMatchOptions): void {
    if (!this.room) throw new Error("enterMatch() called before connect()");
    const {
      driverName,
      blueprint,
      weaponUpgrades = DEFAULT_WEAPON_UPGRADES,
      preferredTeam = 0,
      preferLeader = false,
    } = opts;
    this.room.send("hello", {
      name: driverName,
      blueprint,
      weaponUpgrades,
      preferredTeam,
      ...(preferLeader ? { preferLeader: true } : {}),
    });
  }

  // ── Hosted lobby (HostedArenaRoom) ──────────────────────────────────────────

  /**
   * HOST path: create a fresh private hosted room. Does NOT send `hello` —
   * call {@link enterMatch} (with `preferLeader: true`) once handlers are
   * subscribed, exactly like the queue flow's connect/enterMatch split.
   * The room replies with `welcome` (+ `lobbyCode`) after the hello.
   */
  async createHosted(opts: HostedConnectOptions): Promise<void> {
    const { serverUrl, driverName, authToken = null } = opts;
    this.client = new Client(serverUrl);
    // Room-level `name` must match the hello's `name` (lobby-upgrade-plan §8b gotcha 5).
    const createPromise = this.client.create("hosted", { name: driverName, token: authToken });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Server not responding — try again in a moment")), 12_000),
    );
    const room = await Promise.race([createPromise, timeoutPromise]);
    this.attachHostedRoom(room);
  }

  /**
   * JOIN path: resolve a 5-char join code via POST /api/lobby/resolve-code,
   * then `joinById` the private room (hosted rooms are unlisted — joinById is
   * the only way in). Guests are fine: `token: null` passes onAuth.
   */
  async joinHostedByCode(code: string, opts: HostedConnectOptions): Promise<void> {
    const { serverUrl, driverName, authToken = null } = opts;
    const roomId = await resolveHostedCode(serverUrl, code);
    this.client = new Client(serverUrl);
    const joinPromise = this.client.joinById(roomId, { name: driverName, token: authToken });
    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("Server not responding — try again in a moment")), 12_000),
    );
    const room = await Promise.race([joinPromise, timeoutPromise]);
    this.attachHostedRoom(room);
  }

  /** Wire every message the hosted room sends into the instance-held listeners. */
  private attachHostedRoom(room: Room): void {
    this.room = room;
    this.leftByUs = false;

    room.onMessage("validation-error", (msg: { errors: string[] }) => {
      console.warn("server rejected blueprint:", msg.errors);
    });

    room.onMessage("welcome",            (p: HostedWelcomePayload)      => { for (const fn of this.hostedWelcomeListeners) fn(p); });
    room.onMessage("lobbyCode",          (p: { code: string })          => { for (const fn of this.lobbyCodeListeners) fn(p); });
    room.onMessage("roster",             (p: HostedRosterPayload)       => { for (const fn of this.hostedRosterListeners) fn(p); });
    room.onMessage("youAreLeader",       ()                             => { for (const fn of this.youAreLeaderListeners) fn(); });
    room.onMessage("leaderLeft",         (p: LeaderLeftPayload)         => { for (const fn of this.leaderLeftListeners) fn(p); });
    room.onMessage("lobbyStartRejected", (p: LobbyStartRejectedPayload) => { for (const fn of this.lobbyStartRejectedListeners) fn(p); });
    room.onMessage("queueState",         (p: HostedQueueStatePayload)   => { for (const fn of this.hostedQueueStateListeners) fn(p); });
    room.onMessage("start",              (p: HostedStartPayload)        => { for (const fn of this.hostedStartListeners) fn(p); });
    room.onMessage("roundEnd",           (p: HostedRoundEndPayload)     => { for (const fn of this.hostedRoundEndListeners) fn(p); });

    room.onLeave((code: number) => {
      for (const fn of this.hostedLeaveListeners) fn(code ?? 0, this.leftByUs);
    });

    this.subscribeChat(room);
  }

  /** `ready {ready?}` — omit the flag to toggle (ShooterRoom parity on the server). */
  sendReady(ready?: boolean): void {
    if (!this.room) return;
    this.room.send("ready", ready === undefined ? {} : { ready });
  }

  /** `lobbyConfig` — leader only; the server clamps and rebroadcasts via `roster`. */
  sendLobbyConfig(settings: Partial<LobbySettingsPayload>): void {
    if (!this.room) return;
    this.room.send("lobbyConfig", settings);
  }

  /** `lobbyStart {force?}` — leader only. In the `post` phase this is the REMATCH. */
  sendLobbyStart(force = false): void {
    if (!this.room) return;
    this.room.send("lobbyStart", force ? { force: true } : {});
  }

  /** `lobbyReturn` — leader only, `post` phase only: back to the lobby early. */
  sendLobbyReturn(): void {
    if (!this.room) return;
    this.room.send("lobbyReturn", {});
  }

  onHostedWelcome(fn: (p: HostedWelcomePayload) => void): void { this.hostedWelcomeListeners.push(fn); }
  onLobbyCode(fn: (p: { code: string }) => void): void { this.lobbyCodeListeners.push(fn); }
  onLobbyRoster(fn: (p: HostedRosterPayload) => void): void { this.hostedRosterListeners.push(fn); }
  onYouAreLeader(fn: () => void): void { this.youAreLeaderListeners.push(fn); }
  onLeaderLeft(fn: (p: LeaderLeftPayload) => void): void { this.leaderLeftListeners.push(fn); }
  onLobbyStartRejected(fn: (p: LobbyStartRejectedPayload) => void): void { this.lobbyStartRejectedListeners.push(fn); }
  onHostedQueueState(fn: (p: HostedQueueStatePayload) => void): void { this.hostedQueueStateListeners.push(fn); }
  onHostedStart(fn: (p: HostedStartPayload) => void): void { this.hostedStartListeners.push(fn); }
  onHostedRoundEnd(fn: (p: HostedRoundEndPayload) => void): void { this.hostedRoundEndListeners.push(fn); }
  onHostedLeave(fn: (code: number, byUs: boolean) => void): void { this.hostedLeaveListeners.push(fn); }

  isConnected(): boolean {
    return !!this.room;
  }

  ownId(): string {
    return this.room?.sessionId ?? "";
  }

  sendState(payload: ClientStateUploadPayload): void {
    if (!this.room) return;
    this.room.send("state", payload);
  }

  equip(blueprint: VehicleBlueprint): void {
    if (!this.room) return;
    this.room.send("equip", { blueprint });
  }

  sendChat(text: string): void {
    if (!this.room) return;
    this.room.send("chat", { text });
  }

  sendCollision(payload: { targetId: string; approachSpeed: number; nx: number; nz: number }): void {
    if (!this.room) return;
    this.room.send("collision", payload);
  }

  sendSelfCollision(payload: { approachSpeed: number; impactSeverity: number; nx: number; nz: number; tag: string }): void {
    if (!this.room) return;
    this.room.send("selfCollision", payload);
  }

  sendRoundReset(): void {
    if (!this.room) return;
    this.room.send("round-reset");
  }

  onChat(fn: (msg: ChatMessageView) => void): void {
    this.chatListeners.push(fn);
  }

  onQueueState(fn: (p: QueueStatePayload) => void): void { this.queueStateListeners.push(fn); }
  onQueuePos(fn: (p: QueuePosPayload) => void): void { this.queuePosListeners.push(fn); }
  onSeat(fn: (p: SeatPayload) => void): void { this.seatListeners.push(fn); }
  onRoundStart(fn: (p: QueueStartPayload) => void): void { this.startListeners.push(fn); }
  onRoundEnd(fn: (p: RoundEndPayload) => void): void { this.roundEndListeners.push(fn); }

  snapshot(): NetworkSnapshot {
    const players = new Map<string, PlayerView>();
    const projectiles = new Map<string, ProjectileView>();
    const mines = new Map<string, MineView>();
    const powerUps = new Map<string, PowerUpView>();
    const recentFx: NetworkSnapshot["recentFx"] = [];
    const teamScores: NetworkSnapshot["teamScores"] = { 1: 0, 2: 0 };
    if (!this.room) {
      return { serverTime: 0, ownId: "", players, projectiles, mines, powerUps, recentFx, teamScores };
    }
    const state: any = this.room.state;
    if (state?.players?.forEach) {
      state.players.forEach((p: any, id: string) => {
        players.set(id, {
          id,
          name: p.name,
          blueprintId: p.blueprintId,
          x: p.x,
          y: p.y,
          z: p.z,
          rotY: p.rotY,
          vx: p.vx,
          vy: p.vy,
          vz: p.vz,
          health: p.health,
          maxHealth: p.maxHealth,
          score: p.score,
          alive: p.alive,
          reloadRemaining: p.reloadRemaining,
          lives: p.lives ?? 3,
          eliminated: p.eliminated ?? false,
          nextRespawnAt: p.nextRespawnAt ?? 0,
          boostEnergy: p.boostEnergy ?? 100,
          boostMax: p.boostMax ?? 100,
          teamId: p.teamId ?? 0,
          lastFiredAt: p.lastFiredAt ?? 0,
          deaths: p.deaths ?? 0,
          damageFront: p.damageFront ?? 0,
          damageBody:  p.damageBody  ?? 0,
          damageRear:  p.damageRear  ?? 0,
          mineCount: p.mineCount ?? 5,
          homingMissiles: p.homingMissiles ?? 0,
          wideMineActive: p.wideMineActive ?? false,
          armour: p.armour ?? 0,
          overdriveUntil: p.overdriveUntil ?? 0,
          spread5Until: p.spread5Until ?? 0,
          rocketAmmo: p.rocketAmmo ?? 0,
          ricochetShots: p.ricochetShots ?? 0,
          shockwaveCharges: p.shockwaveCharges ?? 0,
          reloadRemaining2: p.reloadRemaining2 ?? 0,
        });
      });
    }
    if (state?.projectiles?.forEach) {
      state.projectiles.forEach((p: any, id: string) => {
        projectiles.set(id, {
          id,
          ownerId: p.ownerId,
          x: p.x,
          y: p.y,
          z: p.z,
          dx: p.dx,
          dy: p.dy,
          dz: p.dz,
          speed: p.speed,
          spawnedAt: p.spawnedAt,
          isHoming: p.isHoming ?? false,
          weaponType: p.weaponType ?? "",
          homingTargetId: p.homingTargetId ?? "",
        });
      });
    }
    if (state?.mines?.forEach) {
      state.mines.forEach((m: any, id: string) => {
        mines.set(id, {
          id,
          ownerId: m.ownerId,
          x: m.x,
          z: m.z,
          deployedAt: m.deployedAt,
        });
      });
    }
    if (state?.powerUps?.forEach) {
      state.powerUps.forEach((pu: any, id: string) => {
        powerUps.set(id, {
          id,
          type: pu.type,
          x: pu.x,
          z: pu.z,
          available: pu.available,
        });
      });
    }
    if (state?.recentFx?.forEach) {
      state.recentFx.forEach((fx: any) => {
        recentFx.push({
          kind: fx.kind,
          x: fx.x,
          y: fx.y,
          z: fx.z,
          createdAt: fx.createdAt,
        });
      });
    }
    teamScores[1] = state?.teamScore1 ?? 0;
    teamScores[2] = state?.teamScore2 ?? 0;
    return {
      serverTime: state?.serverTime ?? 0,
      ownId: this.room.sessionId,
      players,
      projectiles,
      mines,
      powerUps,
      recentFx,
      teamScores,
    };
  }

  consumeFreshFx(snapshot: NetworkSnapshot): typeof snapshot.recentFx {
    const fresh: typeof snapshot.recentFx = [];
    for (const fx of snapshot.recentFx) {
      if (this.latestFxSeen.has(fx.kind)) continue;
      this.latestFxSeen.add(fx.kind);
      fresh.push(fx);
    }
    if (this.latestFxSeen.size > 256) {
      const arr = [...this.latestFxSeen];
      this.latestFxSeen = new Set(arr.slice(arr.length - 128));
    }
    return fresh;
  }

  leave(): void {
    this.leftByUs = true;
    this.room?.leave();
    this.room = null;
  }
}
